import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';

import './index.scss';

// TODO: 替换兜底图片
import ICON_DEFAULT_GAME from '@user-center/assets/svgs/default-img.svg';

/**
 * 图片加载状态枚举
 */
export enum ImageLoadState {
  /** 加载中 */
  LOADING = 'loading',
  /** 加载成功 */
  SUCCESS = 'success',
  /** 加载失败 */
  ERROR = 'error',
}

/**
 * 图片组件Props接口
 */
export interface IImageProps extends IBaseProps {
  /** 是否显示加载动画 */
  showLoading?: boolean;
  /** 自定义默认图片base64或URL */
  defaultImg?: string;
  /** 图片alt属性 */
  alt?: string;
  /** 图片源地址 */
  src: string;
  /** 图片加载成功回调 */
  onLoad?: () => void;
  /** 图片加载失败回调 */
  onError?: (error: Error) => void;
  /** 点击事件 */
  onClick?: () => void;
}

/**
 * 图片组件
 * 基于HTML、SCSS实现，支持加载动画和异常兜底图片
 */
const Image: React.FC<IImageProps> = (props) => {
  const {
    style,
    className,
    showLoading = true,
    defaultImg = ICON_DEFAULT_GAME,
    alt = '',
    src,
    onLoad,
    onError,
    onClick,
  } = props;

  const isLoadSuccess = useRef(false);
  const [loadState, setLoadState] = useState<ImageLoadState>(ImageLoadState.LOADING);
  const [imageSrc, setImageSrc] = useState<string>(src);

  /**
   * 处理图片加载成功
   */
  const handleImageLoad = () => {
    isLoadSuccess.current = true;
    setLoadState(ImageLoadState.SUCCESS);
    onLoad?.();
  };

  /**
   * 处理图片加载失败
   */
  const handleImageError = () => {
    isLoadSuccess.current = false;
    setLoadState(ImageLoadState.ERROR);
    setImageSrc(defaultImg);
    onError?.(new Error('image load failure.'));
  };

  /**
   * 处理图片点击
   */
  const handleImageClick = () => {
    onClick?.();
  };

  /**
   * 重置加载状态
   */
  useEffect(() => {
    if (isLoadSuccess.current) {
      return;
    }

    if (!src) {
      setLoadState(ImageLoadState.ERROR);
      setImageSrc(defaultImg);
      return;
    }

    setLoadState(ImageLoadState.LOADING);
    setImageSrc(src);

    return () => {
      isLoadSuccess.current = false;
      setLoadState(ImageLoadState.LOADING);
    };
  }, [src]);

  /**
   * 渲染加载动画
   */
  const renderLoading = () => {
    if (!showLoading || loadState !== ImageLoadState.LOADING) {
      return null;
    }

    // TODO: 替换为loading组件
    return <img src="" className="dbu-image__loading" alt="" />;
  };

  return (
    <div
      style={style}
      className={classnames('dbu-image', className, [`dbu-image--${loadState}`])}
      onClick={handleImageClick}
    >
      {renderLoading()}
      <img
        style={{
          display: loadState === ImageLoadState.LOADING && showLoading ? 'none' : 'block',
        }}
        className="dbu-image__img"
        src={imageSrc}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

export default Image;
